/** Name and notes rules the Resource editor shares with the server. */
import { RESOURCE_NOTES_MAX, resourceNotesLength } from '@/lib/resourceEditorPayload';
import type { ResourceKind } from '@/lib/types';

const clock = (seconds:number) => {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};
/** The server's default name (`feedback_resources._label`): what recipients see
 * when Name stays empty. */
export function resourceDefaultLabel(kind:ResourceKind, value:{ timestampSeconds?:number; window?:{ start:number; end:number } }):string {
  if (kind === 'tactical_board') return 'Tactical board';
  if (kind === 'moment') return `Moment at ${clock(value.timestampSeconds ?? 0)}`;
  return `Clip ${clock(value.window?.start ?? 0)}–${clock(value.window?.end ?? 0)}`;
}
/** A version saved with an empty Name carries the server default as its name.
 * Read it back as empty, so the default keeps following the playhead or trim. */
export function editableResourceLabel(kind:ResourceKind, saved:string,
  value:{ timestampSeconds?:number; window?:{ start:number; end:number } }):string {
  return saved === resourceDefaultLabel(kind, value) ? '' : saved;
}

/** Keep an edit within the 2 000-codepoint limit by cutting only the newly
 * inserted span; the coach's existing text before and after it stays. */
export function fitResourceNotes(previous:string, next:string):string {
  if (resourceNotesLength(next) <= RESOURCE_NOTES_MAX) return next;
  const before = [...previous], after = [...next];
  let head = 0;
  while (head < before.length && head < after.length && before[head] === after[head]) head++;
  let tail = 0;
  while (tail < before.length - head && tail < after.length - head
    && before[before.length - 1 - tail] === after[after.length - 1 - tail]) tail++;
  const prefix = after.slice(0, head), inserted = after.slice(head, after.length - tail), suffix = after.slice(after.length - tail);
  const fits = (keep:number) => resourceNotesLength([...prefix, ...inserted.slice(0, keep), ...suffix].join('')) <= RESOURCE_NOTES_MAX;
  let low = 0, high = inserted.length;
  while (low < high) { const mid = Math.ceil((low + high) / 2); if (fits(mid)) low = mid; else high = mid - 1; }
  const fitted = [...prefix, ...inserted.slice(0, low), ...suffix].join('');
  return resourceNotesLength(fitted) <= RESOURCE_NOTES_MAX ? fitted : previous;
}

const RETURN_REFUSED:Record<string, string> = {
  'resource-limit':'Your feedback draft already has ten Resources, so this one is saved in your library but not pinned.',
  'replacement-required':'Your feedback draft pins another version of this Resource. Use Update version there; this one is saved but not pinned.',
  'inactive-target':'Your feedback draft was not open to receive it. Your Resource is saved; use Back to feedback to pin it.',
  'departed-target':'Your feedback draft is no longer available here. Your Resource is saved in your library.',
  'unavailable-resource':'This Resource can no longer be pinned. Saved work is kept.',
  'link-failed':'Your Resource is saved but could not be pinned. Use Back to feedback to try again.',
  unavailable:'Your Resource is saved. The feedback draft could not be opened; use Back to feedback to try again.',
  cancelled:'',
};
/** A return to the feedback draft that did not pin (plan F2). Its message is
 * what the editor shows; an empty one (cancelled) shows nothing. */
export class ResourceReturnError extends Error {
  constructor(readonly reason:string) {
    super(RETURN_REFUSED[reason] ?? RETURN_REFUSED['link-failed']);
    this.name = 'ResourceReturnError';
  }
}
